import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getToken, ApiError } from '../api.js';
import { apiFailure } from '../lib/apiError.js';
import { ViewLink } from '../components/ViewLink.jsx';
import { statusLabel, isInFlight, canCancel, uploadProblem, isImage } from '../lib/codeTab.js';

/**
 * The Code tab: ask for a change to this product, in a conversation, with
 * pasted screenshots or attached files. A separate builder picks the request up
 * and reports back into the same thread.
 *
 * Only the people on the server's CODE_TAB_EMAILS allowlist ever get here (the
 * Shell draws the entry from `session.codeTab`, and the API answers 404 to
 * anyone else on every call below). Everything from the server, including the
 * builder's own words, is rendered as plain text by React; nothing is
 * injected as HTML and nothing is rendered as markdown. Files are fetched with the session
 * header and shown from a blob URL (images) or saved (everything else).
 */

const when = (iso) => new Date(iso).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' });
const AUTHOR = (m) => (m.author === 'claude' ? 'Claude' : m.author === 'system' ? 'Ledger' : m.authorEmail ?? 'User');

// Uploads send raw bytes, which the shared JSON client cannot.
async function uploadFile(file) {
  const problem = uploadProblem(file);
  if (problem) throw new Error(problem);
  const res = await fetch('/api/code/files', {
    method: 'POST',
    headers: { authorization: `Bearer ${getToken()}`, 'content-type': file.type, 'x-file-name': encodeURIComponent(file.name) },
    body: file,
  });
  const payload = await res.json().catch(() => null);
  if (!res.ok) {
    const { message, code } = apiFailure(payload, res.status, 'upload failed');
    throw new ApiError(payload?.message ?? message, res.status, code, payload);
  }
  return payload;
}

async function fetchFile(id) {
  const res = await fetch(`/api/code/files/${id}`, { headers: { authorization: `Bearer ${getToken()}` } });
  if (!res.ok) throw new Error('Could not load the file');
  return res.blob();
}

/** Attach button, paste and drop in one place, used by both the new-request and reply boxes. */
function useAttachments() {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState(null);

  const add = async (list) => {
    setError(null);
    for (const f of list) {
      setUploading((n) => n + 1);
      try {
        const saved = await uploadFile(f);
        setFiles((cur) => [...cur, saved]);
      } catch (e) {
        setError(e.message);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };
  const onPaste = (e) => {
    const pasted = [...e.clipboardData.files];
    if (pasted.length > 0) { e.preventDefault(); add(pasted); }
  };
  const onDrop = (e) => {
    if (e.dataTransfer.files.length > 0) { e.preventDefault(); add([...e.dataTransfer.files]); }
  };
  return {
    files, uploading, error, add, onPaste, onDrop,
    remove: (id) => setFiles((c) => c.filter((f) => f.id !== id)),
    clear: () => setFiles([]),
  };
}

function Attacher({ a }) {
  const input = useRef(null);
  return (
    <div className="code-attach">
      <input ref={input} type="file" multiple hidden
             onChange={(e) => { a.add([...(e.target.files ?? [])]); e.target.value = ''; }} />
      <button type="button" className="btn btn--quiet" onClick={() => input.current?.click()}>
        Attach files or screenshots
      </button>
      <span className="code-hint">You can also paste a screenshot into the box, or drop files on it.</span>
      {a.uploading > 0 && <span className="code-hint">Uploading…</span>}
      {a.error && <div className="error">{a.error}</div>}
      <ul className="code-files">
        {a.files.map((f) => (
          <li key={f.id}>
            {f.fileName}{' '}
            <button type="button" className="btn btn--quiet" onClick={() => a.remove(f.id)}
                    aria-label={`Remove ${f.fileName}`}>Remove</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Images show inline; everything else is a download. Both fetch with the session header. */
function FileChip({ file }) {
  const [url, setUrl] = useState(null);
  const image = isImage(file.contentType);
  useEffect(() => {
    if (!image) return undefined;
    let made = null;
    let live = true;
    fetchFile(file.id).then((b) => {
      if (!live) return;
      made = URL.createObjectURL(b);
      setUrl(made);
    }).catch(() => undefined);
    return () => { live = false; if (made) URL.revokeObjectURL(made); };
  }, [file.id, image]);

  if (image) return url ? <img className="code-shot" src={url} alt={file.fileName} /> : <span className="code-hint">{file.fileName}</span>;
  return (
    <button type="button" className="btn btn--quiet" onClick={async () => {
      const blob = await fetchFile(file.id);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = file.fileName;
      a.click();
      URL.revokeObjectURL(a.href);
    }}>{file.fileName}</button>
  );
}

function NewRequest({ onCreated }) {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const a = useAttachments();

  const submit = async () => {
    setSending(true); setError(null);
    try {
      onCreated(await api.post('/api/code/requests', { title, body, attachmentIds: a.files.map((f) => f.id) }));
    } catch (e) {
      setError(e.message);
    } finally { setSending(false); }
  };
  return (
    <div onPaste={a.onPaste} onDrop={a.onDrop} onDragOver={(e) => e.preventDefault()}>
      <h2 className="code-h">What would you like changed?</h2>
      <p className="code-hint">
        Changes are built and put on the live system. Describe what you want as you would to a
        colleague and attach anything that helps. You will get an email when it is done.
      </p>
      {error && <div className="error">{error}</div>}
      <label className="field"><span>Short title</span>
        <input value={title} maxLength={140} onChange={(e) => setTitle(e.target.value)} />
      </label>
      <label className="field"><span>Details</span>
        <textarea rows={8} maxLength={20000} value={body} onChange={(e) => setBody(e.target.value)} />
      </label>
      <Attacher a={a} />
      <button type="button" className="btn" disabled={sending || a.uploading > 0 || !title.trim() || !body.trim()} onClick={submit}>
        {sending ? 'Sending…' : 'Send request'}
      </button>
    </div>
  );
}

function Thread({ id, onChanged }) {
  const [thread, setThread] = useState(null);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const a = useAttachments();

  const load = useCallback(() => {
    api.get(`/api/code/requests/${id}`).then((t) => { setThread(t); setError(null); }).catch((e) => setError(e.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const inFlight = thread ? isInFlight(thread.status) : false;
  useEffect(() => {
    if (!inFlight) return undefined;
    const t = setInterval(() => { load(); onChanged(); }, 4000);
    return () => clearInterval(t);
  }, [inFlight, load, onChanged]);

  const send = async () => {
    setSending(true);
    try {
      await api.post(`/api/code/requests/${id}/messages`, { body: reply, attachmentIds: a.files.map((f) => f.id) });
      setReply(''); a.clear(); load(); onChanged();
    } catch (e) { setError(e.message); } finally { setSending(false); }
  };
  const cancel = () => api.post(`/api/code/requests/${id}/cancel`, {})
    .then(() => { load(); onChanged(); }).catch((e) => setError(e.message));

  if (!thread) return error ? <div className="error">{error}</div> : <div className="empty">Loading…</div>;
  return (
    <div onPaste={a.onPaste} onDrop={a.onDrop} onDragOver={(e) => e.preventDefault()}>
      <h2 className="code-h">{thread.title}</h2>
      <p>
        <span className={`code-status code-status--${thread.status.toLowerCase()}`}>{statusLabel(thread.status)}</span>
        {thread.statusDetail && <span className="code-hint"> {thread.statusDetail}</span>}{' '}
        {canCancel(thread.status) && <button type="button" className="btn btn--quiet" onClick={cancel}>Cancel</button>}
      </p>
      {error && <div className="error">{error}</div>}
      {thread.summary && <div className="note"><strong>What was done</strong><p className="code-text">{thread.summary}</p></div>}
      <ol className="code-messages">
        {thread.messages.map((m) => (
          <li key={m.id} className={`code-msg code-msg--${m.author}`}>
            <span className="code-hint">{AUTHOR(m)} · {when(m.createdAt)}</span>
            <p className="code-text">{m.body}</p>
            {m.attachments.map((f) => <FileChip key={f.id} file={f} />)}
          </li>
        ))}
      </ol>
      <label className="field">
        <span>{thread.status === 'NEEDS_INPUT' ? 'Your answer' : 'Ask for something more or different'}</span>
        <textarea rows={4} maxLength={20000} value={reply} onChange={(e) => setReply(e.target.value)} />
      </label>
      <Attacher a={a} />
      <button type="button" className="btn" disabled={sending || a.uploading > 0 || !reply.trim()} onClick={send}>
        {sending ? 'Sending…' : 'Send'}
      </button>
    </div>
  );
}

/** Request list on the left, the open conversation on the right. Address: /code and /code/<id>. */
export function Code({ entityId, requestId, onView }) {
  const [requests, setRequests] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    api.get('/api/code/requests').then(setRequests).catch((e) => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load, requestId]);

  // Refresh the list while anything is being built, so a status change shows up unprompted.
  const busy = requests?.some((r) => isInFlight(r.status)) ?? false;
  useEffect(() => {
    if (!busy) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [busy, load]);

  return (
    <>
      <header className="rhead">
        <h1 className="rhead__title display">Code</h1>
        <div className="rhead__sub">Ask for changes to Ledger. They are built, checked and put live.</div>
      </header>
      <div className="code-tab">
        <aside className="code-list">
          <ViewLink className="btn btn--quiet" view="code" entityId={entityId} onNavigate={() => onView('code')}>
            New request
          </ViewLink>
          {error && <div className="error">{error}</div>}
          {requests?.length === 0 && <p className="code-hint">Nothing asked for yet.</p>}
          <ul>
            {requests?.map((r) => (
              <li key={r.id} className={r.id === requestId ? 'code-active' : ''}>
                <ViewLink view="code" entityId={entityId} arg={{ codeId: r.id }}
                          aria-current={r.id === requestId ? 'page' : undefined}
                          onNavigate={() => onView('code', { codeId: r.id })}>
                  <strong>{r.title}</strong>
                  <span className={`code-status code-status--${r.status.toLowerCase()}`}>{statusLabel(r.status)}</span>
                  <span className="code-hint">{r.requestedByEmail} · {when(r.createdAt)}</span>
                </ViewLink>
              </li>
            ))}
          </ul>
        </aside>
        <section className="code-thread">
          {requestId
            ? <Thread key={requestId} id={requestId} onChanged={load} />
            : <NewRequest onCreated={(r) => { load(); onView('code', { codeId: r.id }); }} />}
        </section>
      </div>
    </>
  );
}
